/**
 * DieselComponents.tsx
 * Shared UI components used across all diesel pages.
 */
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { brand, neutral, ink, status as statusTokens, urgency as urgencyTokens, type as typeScale, radius as radiusTokens, shadow as shadowTokens, font } from '../../styles/dieselTokens';
import type { AlertSeverity, AlertType, CycleReconciliation, DieselAlert } from '../../types/diesel.types';

// ─── Helpers ──────────────────────────────────────────────────────────────────
export function fmtNum(v: number | null | undefined, dec = 0): string {
  if (v === null || v === undefined) return '—';
  return v.toLocaleString('en', { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

export function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

export function fmtDate(s: string | undefined): string {
  if (!s) return '—';
  return new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function varianceClass(pct: number | null | undefined): string {
  if (pct === null || pct === undefined) return '';
  const abs = Math.abs(pct);
  if (abs <= 0.05) return 'var-ok';
  if (abs <= 0.15) return 'var-warn';
  return 'var-danger';
}

// ─── Severity colours ─────────────────────────────────────────────────────────
const SEVERITY_STYLE: Record<AlertSeverity, { bg: string; text: string; dot: string }> = {
  critical: { bg: statusTokens.danger.bg, text: statusTokens.danger.text, dot: urgencyTokens.critical.fg },
  high:     { bg: statusTokens.warning.bg, text: statusTokens.warning.text, dot: urgencyTokens.high.fg },
  medium:   { bg: statusTokens.info.bg, text: '#1e3a8a', dot: urgencyTokens.medium.fg },
  low:      { bg: neutral[100], text: neutral[700], dot: neutral[500] },
  info:     { bg: '#d1fae5', text: '#065f46', dot: '#059669' },
};

// Values are line-icon keys (see ICON_PATHS further down this file), not
// emoji — every alert type renders through the same thin-stroke glyph set
// as the rest of the app instead of a colorful pictograph.
type LineIconKey = keyof typeof ICON_PATHS;
const ALERT_ICONS: Partial<Record<AlertType, LineIconKey>> = {
  LOW_FUEL:               'fuel',
  ZERO_GRID_24H:          'alert',
  CONSUMPTION_OVER_CCPH:  'trend',
  REFUEL_MISMATCH_CMS:    'trend',
  REFUEL_MISMATCH_TOMCARD:'card',
  MISSING_GRATO:          'doc',
  MISSING_CMS:            'cpu',
  FAULTY_METER:           'engine',
  THEFT_SUSPECTED:        'alert',
  RH_MISMATCH:            'chart',
  GENERATOR_MOVED:        'trend',
  IMPORT_ERROR:           'x',
  DATA_IMPORTED:          'check-shield',
};

// ─── CycleSelector ────────────────────────────────────────────────────────────
interface CycleSelectorProps {
  cycles: Array<{ cycle_key: string; label: string; status: string }>;
  value: string;
  onChange: (key: string) => void;
}
export function CycleSelector({ cycles, value, onChange }: CycleSelectorProps) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <span style={{ fontSize: 13, color: neutral[500], fontWeight: 500 }}>Cycle:</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        aria-label="Select billing cycle"
        style={{ border: `1px solid ${neutral[200]}`, borderRadius: 8, padding: '6px 12px', fontSize: 13, background: '#fff', cursor: 'pointer' }}
      >
        {cycles.map(c => (
          <option key={c.cycle_key} value={c.cycle_key}>
            {c.label} {c.status !== 'open' ? `(${c.status})` : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── SiteSelect ───────────────────────────────────────────────────────────────
// A searchable site picker — a plain <select> with 100+ sites forces
// scrolling through the whole list to find one. This is a type-to-filter
// combobox instead: type any part of the site name, ID, or cluster and
// pick from the narrowed list, same interaction as a browser address bar.
export interface SiteSelectOption { id: string; label: string; sub?: string }
interface SiteSelectProps {
  options: SiteSelectOption[];
  value: string;
  onChange: (id: string) => void;
  loading?: boolean;
  placeholder?: string;
}
export function SiteSelect({ options, value, onChange, loading, placeholder = 'Search by site name, ID or cluster…' }: SiteSelectProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const selected = options.find(o => o.id === value);

  // Keep the input text in sync with the current selection whenever it's
  // not actively being edited — e.g. a "recent activity" row elsewhere on
  // the page picks a site programmatically, or the cycle changes and
  // clears the selection.
  useEffect(() => {
    if (!open) setQuery(selected ? selected.label : '');
  }, [selected?.id, open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const q = query.trim().toLowerCase();
  const filtered = !q ? options : options.filter(o =>
    o.label.toLowerCase().includes(q) || o.id.toLowerCase().includes(q) || (o.sub || '').toLowerCase().includes(q)
  );
  const shown = filtered.slice(0, 50);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setOpen(true); setQuery(''); }}
        placeholder={loading ? 'Loading sites…' : placeholder}
        disabled={loading}
        aria-label="Search and select a site"
        style={{ width: '100%', border: `1px solid ${neutral[200]}`, borderRadius: 8, padding: '8px 32px 8px 12px', fontSize: 13, boxSizing: 'border-box' }}
      />
      {selected && !open && (
        <button
          type="button"
          onClick={() => { onChange(''); setQuery(''); }}
          aria-label="Clear selected site"
          style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: neutral[400], cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}
        >×</button>
      )}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, maxHeight: 280, overflowY: 'auto',
          background: '#fff', border: `1px solid ${neutral[200]}`, borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', zIndex: 20,
        }}>
          {shown.length === 0 ? (
            <div style={{ padding: '10px 12px', fontSize: 12, color: neutral[400] }}>No matching sites</div>
          ) : (
            shown.map(o => (
              <div
                key={o.id}
                onMouseDown={e => { e.preventDefault(); onChange(o.id); setQuery(o.label); setOpen(false); }}
                style={{ padding: '8px 12px', fontSize: 13, cursor: 'pointer', background: o.id === value ? neutral[50] : 'transparent' }}
                onMouseEnter={e => (e.currentTarget.style.background = neutral[50])}
                onMouseLeave={e => (e.currentTarget.style.background = o.id === value ? neutral[50] : 'transparent')}
              >
                <div style={{ fontWeight: 600, color: ink }}>{o.label}</div>
                {o.sub && <div style={{ fontSize: 11, color: neutral[400] }}>{o.sub}</div>}
              </div>
            ))
          )}
          {filtered.length > shown.length && (
            <div style={{ padding: '6px 12px', fontSize: 11, color: neutral[400], borderTop: `1px solid ${neutral[100]}` }}>
              +{filtered.length - shown.length} more — keep typing to narrow
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── SeverityBadge ────────────────────────────────────────────────────────────
export function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const s = SEVERITY_STYLE[severity] || SEVERITY_STYLE.info;
  return (
    <span style={{ background: s.bg, color: s.text, padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.dot, display: 'inline-block' }} />
      {severity.toUpperCase()}
    </span>
  );
}

// ─── AlertCard ────────────────────────────────────────────────────────────────
interface AlertCardProps {
  alert: DieselAlert;
  onAcknowledge?: (id: string) => void;
  onResolve?: (id: string) => void;
  onDismiss?: (id: string) => void;
  compact?: boolean;
  isAdmin?: boolean;
}
export function AlertCard({ alert, onAcknowledge, onResolve, onDismiss, compact, isAdmin }: AlertCardProps) {
  const [showResolve, setShowResolve] = useState(false);
  const [note, setNote] = useState('');
  const navigate = useNavigate();
  const iconKey = ALERT_ICONS[alert.alert_type] || 'bell';
  const canInvestigate = alert.alert_type === 'THEFT_SUSPECTED' && alert.site_id && alert.cycle_key;

  return (
    <div style={{ background: '#fff', border: `1px solid ${neutral[200]}`, borderRadius: 10, padding: compact ? '12px 16px' : '16px 20px', marginBottom: compact ? 8 : 12 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ width: 36, height: 36, borderRadius: 8, background: SEVERITY_STYLE[alert.severity]?.bg || neutral[100], display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <IconGlyph pathKey={iconKey} size={18} color={SEVERITY_STYLE[alert.severity]?.dot || neutral[500]} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <span style={{ fontWeight: 600, fontSize: 13 }}>{alert.title}</span>
            <SeverityBadge severity={alert.severity} />
            {alert.status !== 'open' && (
              <span style={{ fontSize: 11, color: neutral[500], background: neutral[100], padding: '1px 8px', borderRadius: 10 }}>{alert.status}</span>
            )}
          </div>
          <p style={{ fontSize: 12, color: neutral[600], margin: '0 0 6px', lineHeight: 1.5 }}>{alert.message}</p>
          {!compact && (
            <div style={{ display: 'flex', gap: 6, fontSize: 11, color: neutral[400], flexWrap: 'wrap' }}>
              {alert.site_id  && <span>Site: {alert.site_id}</span>}
              {alert.cluster  && <span>· {alert.cluster}</span>}
              {alert.cycle_key && <span>· Cycle: {alert.cycle_key}</span>}
              <span>· {fmtDate(alert.createdAt)}</span>
              {alert.email_sent && <span>· Email sent</span>}
            </div>
          )}
        </div>
        {alert.status === 'open' && (
          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {onAcknowledge && (
              <button onClick={() => onAcknowledge(alert._id)} style={btnStyle(statusTokens.info.bg, '#1e40af')}>Acknowledge</button>
            )}
            {onResolve && (
              <button onClick={() => setShowResolve(v => !v)} style={btnStyle(statusTokens.success.bg, statusTokens.success.text)}>Resolve</button>
            )}
            {onDismiss && isAdmin && (
              <button
                onClick={() => { if (window.confirm('Dismiss this alert? This hides it without resolving the underlying issue.')) onDismiss(alert._id); }}
                style={btnStyle(neutral[100], neutral[600])}
              >
                Dismiss
              </button>
            )}
          </div>
        )}
        {canInvestigate && (
          <div style={{ flexShrink: 0 }}>
            <button
              onClick={() => navigate(`/diesel/theft-investigation?site_id=${alert.site_id}&cycle_key=${alert.cycle_key}`)}
              style={btnStyle('#fef2f2', '#dc2626')}
            >
              Investigate
            </button>
          </div>
        )}
      </div>
      {showResolve && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f3f4f6' }}>
          <textarea
            value={note} onChange={e => setNote(e.target.value)}
            placeholder="Resolution note (required)…"
            aria-label="Resolution note"
            rows={2}
            style={{ width: '100%', border: `1px solid ${neutral[200]}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, resize: 'vertical', boxSizing: 'border-box' }}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowResolve(false)} style={btnStyle(neutral[100], neutral[700])}>Cancel</button>
            <button onClick={() => { if (note.trim()) { onResolve!(alert._id); setShowResolve(false); } }} style={btnStyle(statusTokens.success.bg, statusTokens.success.text)}>Confirm Resolve</button>
          </div>
        </div>
      )}
    </div>
  );
}

function btnStyle(bg: string, color: string) {
  return { background: bg, color, border: 'none', borderRadius: 6, padding: '4px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' } as React.CSSProperties;
}

// ─── VarianceCell ─────────────────────────────────────────────────────────────
export function VarianceCell({ value, pct, invertColor }: { value: number | null; pct?: number | null; invertColor?: boolean }) {
  if (value === null || value === undefined) return <span style={{ color: neutral[400] }}>—</span>;
  const abs = Math.abs(pct ?? 0);
  let color: string = neutral[700];
  if (abs > 0.15) color = invertColor ? urgencyTokens.ok.fg : urgencyTokens.critical.fg;
  else if (abs > 0.05) color = urgencyTokens.high.fg;
  else color = invertColor ? urgencyTokens.critical.fg : urgencyTokens.ok.fg;
  return (
    <span style={{ color, fontWeight: abs > 0.05 ? 600 : 400, fontFamily: font.mono }}>
      {value > 0 ? '+' : ''}{fmtNum(value, 0)} L
      {pct !== null && pct !== undefined && <span style={{ fontSize: 11, marginLeft: 4 }}>({fmtPct(pct)})</span>}
    </span>
  );
}

// ─── KpiCard ──────────────────────────────────────────────────────────────────
// `tone` is the KPI-tile risk wash from the redesign (risk-ok/risk-warn/
// risk-crit in the prototype) — a border colour + a light background tint
// so a risk count (over-CCPH sites, low-fuel sites, …) reads as a severity
// at a glance, not just a plain number. `alert` (a flat 2px red border,
// no wash) is kept as-is for any existing caller — `tone` is additive, not
// a replacement, so nothing already using this component changes look.
export type KpiTone = 'ok' | 'warn' | 'crit';
const KPI_TONE_STYLE: Record<KpiTone, { border: string; bg: string }> = {
  ok:   { border: urgencyTokens.ok.fg,       bg: urgencyTokens.ok.bg },
  warn: { border: urgencyTokens.high.fg,     bg: urgencyTokens.high.bg },
  crit: { border: urgencyTokens.critical.fg, bg: urgencyTokens.critical.bg },
};
interface KpiCardProps { label: string; value: string | number; sub?: string; color?: string; icon?: string; alert?: boolean; tone?: KpiTone }
export function KpiCard({ label, value, sub, color = ink, icon, alert, tone }: KpiCardProps) {
  const toneStyle = tone ? KPI_TONE_STYLE[tone] : null;
  return (
    <div style={{
      background: toneStyle?.bg || '#fff',
      border: toneStyle ? `1px solid ${toneStyle.border}` : alert ? '2px solid #dc2626' : `1px solid ${neutral[200]}`,
      borderRadius: 12, boxShadow: shadowTokens.card, padding: '18px 22px', flex: 1, minWidth: 140,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <p style={{ fontSize: 12, color: neutral[500], margin: '0 0 6px', fontWeight: 500 }}>{label}</p>
          <p style={{ fontSize: 26, fontWeight: 700, color: toneStyle ? toneStyle.border : color, margin: 0, fontVariantNumeric: 'tabular-nums', fontFamily: font.mono }}>{value}</p>
          {sub && <p style={{ fontSize: 11, color: neutral[400], margin: '4px 0 0' }}>{sub}</p>}
        </div>
        {icon && <ResolvedIcon icon={icon} size={22} color={toneStyle ? toneStyle.border : neutral[400]} />}
      </div>
    </div>
  );
}

// ─── SectionLabel ─────────────────────────────────────────────────────────────
// The small uppercase group header the prototype uses above a KPI cluster
// or in place of a card's <h3> ("BUDGET", "Daily consumption trend", …).
export function SectionLabel({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: neutral[400], marginBottom: 10, ...style }}>
      {children}
    </div>
  );
}

// ─── UploadZone ───────────────────────────────────────────────────────────────
interface UploadZoneProps {
  accept: string;
  label: string;
  subLabel?: string;
  onFile: (f: File) => void;
  uploading?: boolean;
  children?: React.ReactNode;
}
export function UploadZone({ accept, label, subLabel, onFile, uploading, children }: UploadZoneProps) {
  const [drag, setDrag] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false);
    const file = e.dataTransfer.files[0];
    if (file) onFile(file);
  };

  return (
    <div
      onDragOver={e => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={handleDrop}
      onClick={() => ref.current?.click()}
      role="button"
      tabIndex={0}
      aria-label={label}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); ref.current?.click(); } }}
      style={{
        border: `2px dashed ${drag ? urgencyTokens.medium.fg : neutral[200]}`,
        borderRadius: 12, padding: '32px 24px', textAlign: 'center',
        background: drag ? '#eff6ff' : '#fafafa', cursor: 'pointer',
        transition: 'all 0.2s',
      }}
    >
      <input ref={ref} type="file" accept={accept} aria-hidden="true" tabIndex={-1} style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }} />
      <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'center' }}><ResolvedIcon icon={uploading ? '⏳' : '📂'} size={30} color={neutral[400]} /></div>
      <p style={{ fontWeight: 600, margin: '0 0 4px', color: neutral[700] }}>{uploading ? 'Uploading…' : label}</p>
      <p style={{ fontSize: 12, color: neutral[400], margin: 0 }}>Drag & drop or click to browse</p>
      {subLabel && <p style={{ fontSize: 11, color: neutral[400], margin: '4px 0 0' }}>{subLabel}</p>}
      {children}
    </div>
  );
}

// ─── StatusPill ───────────────────────────────────────────────────────────────
export function StatusPill({ status }: { status: string }) {
  const map: Record<string, { bg: string; text: string }> = {
    completed:  statusTokens.success,
    ok:         statusTokens.success,
    open:       statusTokens.danger,
    over:       statusTokens.danger,
    failed:     statusTokens.danger,
    processing: statusTokens.info,
    pending:    statusTokens.warning,
    under:      statusTokens.warning,
    partial:    statusTokens.warning,
    unknown:    statusTokens.neutral,
    resolved:   statusTokens.neutral,
  };
  const { bg, text: color } = map[status?.toLowerCase()] || statusTokens.neutral;
  return <span style={{ background: bg, color, padding: '2px 10px', borderRadius: radiusTokens.pill, fontSize: typeScale.small - 1, fontWeight: 600 }}>{status}</span>;
}

// ─── Likely-cause inference ────────────────────────────────────────────────────
// A short, human label for *why* a site's numbers look the way they do —
// inferred from signals this cycle's reconciliation record already carries
// (instrument quality, theft flag, generator swaps, refuel/grato/CMS gaps,
// grid outages, over/under consumption), the same signals the alert-type
// filter chips on the Reconciliation table already use to classify a row.
// Not a guess from nothing — but also not a multi-cycle trend: this repo's
// reconciliation record is per-cycle, so "drifted the same direction for N
// consecutive cycles" (one of the signals the redesign's own footnote
// mentions) isn't something this data can support without a new backend
// endpoint to look back across cycles.
export type LikelyCauseTone = 'neutral' | 'warn' | 'crit';
export interface LikelyCause { text: string; tone: LikelyCauseTone }
export function inferLikelyCause(recon: CycleReconciliation): LikelyCause {
  if (recon.faulty_meter_days > 0) return { text: 'Instrument — faulty meter', tone: 'warn' };
  if (recon.theft_detected) return { text: 'Possible unlogged consumption', tone: 'crit' };
  if (recon.had_mid_cycle_swap) return { text: 'Generator swap mid-cycle', tone: 'neutral' };
  if (recon.refuel_status === 'discrepancy') return { text: 'Refuel mismatch — field vs CMS', tone: 'warn' };
  if (recon.grato_submission_missing) return { text: 'Missing field visit data', tone: 'warn' };
  if (!recon.has_cms_data) return { text: 'Missing CMS data', tone: 'warn' };
  if (recon.zero_grid_alert) return { text: 'Extended grid outage', tone: 'warn' };
  if (recon.cons_status === 'over') return { text: 'Consumption over contractual — needs review', tone: 'crit' };
  if (recon.cons_status === 'under') return { text: 'Consumption under contractual — needs review', tone: 'warn' };
  return { text: '—', tone: 'neutral' };
}
const LIKELY_CAUSE_VARIANT: Record<LikelyCauseTone, TagVariant> = { neutral: 'neutral', warn: 'warning', crit: 'danger' };
export function LikelyCauseTag({ cause }: { cause: LikelyCause }) {
  if (cause.text === '—') return <span style={{ color: neutral[400] }}>—</span>;
  return <Tag variant={LIKELY_CAUSE_VARIANT[cause.tone]}>{cause.text}</Tag>;
}

// ─── SiteDetailDrawer ─────────────────────────────────────────────────────────
interface DrawerProps {
  recon: CycleReconciliation | null;
  onClose: () => void;
  /** Only pass true when the current user's role matches the backend's
   *  admin-only guard on PATCH /:cycle_key/site/:site_id/override — showing
   *  the review form to someone who'll just get a 403 is worse than not
   *  showing it at all. */
  canReview?: boolean;
  /** Called with the notes text on save. Throw to signal failure — the
   *  drawer shows the error inline and keeps the form open. */
  onSaveReview?: (notes: string) => Promise<void>;
  /** Display name/email offered as the "assign to me" option on the Case
   *  panel's Assigned-to picker below. Optional — the picker still works
   *  (as "Unassigned" only) without it. */
  currentUserName?: string;
}
export function SiteDetailDrawer({ recon, onClose, canReview = false, onSaveReview, currentUserName }: DrawerProps) {
  const [reviewNotes, setReviewNotes] = React.useState('');
  const [reviewSaving, setReviewSaving] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);
  const [showReviewForm, setShowReviewForm] = React.useState(false);
  // Assigned-to / case-status are local-only UI, not backend fields — the
  // API's review endpoint only persists override_notes + the reviewed
  // flag/timestamp. Kept here (reset per site below) so the Case panel can
  // match the redesign's layout without pretending these picks are saved;
  // the note under each field says so plainly.
  const [assignedTo, setAssignedTo] = React.useState('');
  const [caseStatus, setCaseStatus] = React.useState<'open' | 'in_progress' | 'resolved'>('open');

  // Reset the form whenever a different site is opened.
  React.useEffect(() => {
    setReviewNotes('');
    setReviewError(null);
    setShowReviewForm(false);
    setAssignedTo('');
    setCaseStatus(recon?.is_manually_reviewed ? 'resolved' : 'open');
  }, [recon?.site_id, recon?.cycle_key]);

  if (!recon) return null;

  async function handleSaveReview() {
    if (!onSaveReview) return;
    setReviewSaving(true);
    setReviewError(null);
    try {
      await onSaveReview(reviewNotes);
      setShowReviewForm(false);
    } catch (e: any) {
      setReviewError(e?.response?.data?.message || e?.message || 'Failed to save review');
    } finally {
      setReviewSaving(false);
    }
  }

  const rows: [string, any][] = [
    ['Topology',           recon.topology],
    ['Generator',          recon.generator_brand || '—'],
    ['KVA',                recon.dg_kva],
    ['CCPH (L/h)',         fmtNum(recon.ccph, 3)],
    ['Budget RH',          fmtNum(recon.budgeted_rh, 1)],
    ['Field RH',           fmtNum(recon.field_rh, 1)],
    ['CMS RH',             fmtNum(recon.cms_rh, 1)],
    ['Final RH',           fmtNum(recon.final_rh, 1)],
    ['Faulty Meter Days',  recon.faulty_meter_days || 0],
    ['Contractual (L)',    fmtNum(recon.contractual_consumption)],
    ['CMS Consumed (L)',   fmtNum(recon.cms_consumption)],
    ['Field Actual (L)',   fmtNum(recon.field_consumption_actual)],
    ['Cons Variance (L)',  recon.cons_variance != null ? `${recon.cons_variance > 0 ? '+' : ''}${fmtNum(recon.cons_variance)} (${fmtPct(recon.cons_variance_pct)})` : '—'],
    ['Field Added (L)',    fmtNum(recon.field_fuel_added)],
    ['CMS Refuel (L)',     fmtNum(recon.cms_refuel_total)],
    ['Tom Card (L)',       recon.tomcard_purchased != null ? fmtNum(recon.tomcard_purchased) : 'Not mapped'],
    ['Theft (L)',          recon.theft_liters > 0 ? fmtNum(recon.theft_liters) : 'None'],
    ['Grid Avail',         fmtPct(recon.grid_avail_actual)],
    ['Zero Grid Max (h)',  recon.zero_grid_max_streak],
    ['CMS Days',           `${recon.cms_days_covered} / ${recon.cms_days_expected}`],
    ['Field Visits',       recon.field_visits_count],
    ['Last Reconciled',    fmtDate(recon.last_reconciled_at)],
  ];

  const activeAlerts = Object.entries(recon.alerts).filter(([, v]) => v).map(([k]) => k);

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 }} />
      <div style={{ position: 'fixed', right: 0, top: 0, bottom: 0, width: 480, background: '#fff', zIndex: 201, overflowY: 'auto', boxShadow: '-4px 0 20px rgba(0,0,0,0.1)' }}>
        <div style={{ padding: '20px 24px', borderBottom: `1px solid ${neutral[200]}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: ink, color: '#fff' }}>
          <div>
            <p style={{ margin: 0, fontSize: 11, color: '#aaa' }}>{recon.cluster} · {recon.cycle_key}</p>
            <h2 style={{ margin: '4px 0 0', fontSize: 17, fontWeight: 700 }}>{recon.site_name || recon.site_id}</h2>
            <p style={{ margin: '2px 0 0', fontSize: 12, color: '#aaa' }}>{recon.site_id}</p>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 }}>✕ Close</button>
        </div>

        <div style={{ padding: '16px 24px' }}>
          {(() => {
            const cause = inferLikelyCause(recon);
            return cause.text !== '—' ? (
              <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: neutral[500] }}>LIKELY CAUSE</span>
                <LikelyCauseTag cause={cause} />
              </div>
            ) : null;
          })()}

          {activeAlerts.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <p style={{ fontWeight: 600, fontSize: 12, color: neutral[500], marginBottom: 8 }}>ACTIVE ALERTS ({activeAlerts.length})</p>
              {activeAlerts.map(a => {
                const key = ALERT_ICONS[a.toUpperCase().replace(/_([a-z])/g, (_, c) => '_' + c.toUpperCase()) as AlertType] || 'alert';
                return (
                  <div key={a} style={{ background: statusTokens.danger.bg, color: statusTokens.danger.text, borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <IconGlyph pathKey={key} size={13} color={statusTokens.danger.text} />
                    {a.replace(/_/g, ' ')}
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
            {rows.map(([label, value]) => (
              <div key={label} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
                <p style={{ fontSize: 11, color: neutral[400], margin: '0 0 2px' }}>{label}</p>
                <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: ink }}>{value ?? '—'}</p>
              </div>
            ))}
          </div>

          {recon.had_mid_cycle_swap && (
            <div style={{ marginTop: 16, background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: 12 }}>
              <p style={{ fontWeight: 600, fontSize: 12, margin: '0 0 8px', color: statusTokens.warning.text }}>Mid-Cycle Generator Swap Detected</p>
              {recon.swap_segments.map((seg, i) => (
                <div key={i} style={{ fontSize: 12, color: '#78350f', marginBottom: 4 }}>
                  Gen {seg.generator_id}: {fmtNum(seg.rh_in_segment, 1)} RH × {fmtNum(seg.ccph, 3)} CCPH = {fmtNum(seg.consumption_in_segment)} L
                  <span style={{ color: neutral[400], marginLeft: 4 }}>({fmtDate(seg.from_date)} → {fmtDate(seg.to_date)})</span>
                </div>
              ))}
            </div>
          )}

          {/* ── Case ─────────────────────────────────────────────────────────
              Previously this whole drawer was read-only — an investigator
              could SEE a theft-suspected or refuel-mismatch flag here but had
              no way to record "I looked into this, here's what I found"
              without going outside the app entirely. Assigned-to and Status
              below are local-only (no backend field exists for either yet —
              the API only persists override_notes + the reviewed flag/
              timestamp, which Save review still writes for real); each is
              labeled as such rather than implying a save that doesn't happen. */}
          <div style={{ marginTop: 16, borderTop: `1px solid ${neutral[200]}`, paddingTop: 16 }}>
            <p style={{ fontWeight: 600, fontSize: 12, color: neutral[500], marginBottom: 8 }}>CASE</p>

            {recon.is_manually_reviewed && !showReviewForm && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: statusTokens.success.text }}>
                  ✓ Reviewed{recon.reviewed_at ? ` — ${fmtDate(recon.reviewed_at)}` : ''}
                  {recon.reviewed_by && typeof recon.reviewed_by === 'object' && recon.reviewed_by.fullName
                    ? ` by ${recon.reviewed_by.fullName}` : ''}
                </p>
                {recon.override_notes && (
                  <p style={{ margin: 0, fontSize: 12, color: statusTokens.success.text, whiteSpace: 'pre-wrap' }}>{recon.override_notes}</p>
                )}
              </div>
            )}

            {!recon.is_manually_reviewed && !showReviewForm && (
              <p style={{ fontSize: 12, color: neutral[400], marginBottom: 10 }}>Not yet reviewed.</p>
            )}

            {canReview && !showReviewForm && (
              <button
                onClick={() => { setShowReviewForm(true); setReviewNotes(recon.override_notes || ''); }}
                style={{
                  fontSize: 12, fontWeight: 600, color: ink, background: neutral[100],
                  border: `1px solid ${neutral[200]}`, borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
                }}
              >
                {recon.is_manually_reviewed ? 'Update review' : 'Mark as reviewed…'}
              </button>
            )}

            {canReview && showReviewForm && (
              <div>
                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 11, color: neutral[500], marginBottom: 3 }}>Assigned to</label>
                  <select
                    value={assignedTo}
                    onChange={e => setAssignedTo(e.target.value)}
                    aria-label="Assigned to"
                    style={{ width: '100%', border: `1px solid ${neutral[200]}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, boxSizing: 'border-box' as const }}
                  >
                    <option value="">Unassigned</option>
                    {currentUserName && <option value={currentUserName}>{currentUserName} (you)</option>}
                  </select>
                  <p style={{ fontSize: 10.5, color: neutral[400], margin: '3px 0 0' }}>Local to this session only — there's no assignment field on the backend yet.</p>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={{ display: 'block', fontSize: 11, color: neutral[500], marginBottom: 3 }}>Status</label>
                  <select
                    value={caseStatus}
                    onChange={e => setCaseStatus(e.target.value as typeof caseStatus)}
                    aria-label="Case status"
                    style={{ width: '100%', border: `1px solid ${neutral[200]}`, borderRadius: 8, padding: '6px 10px', fontSize: 13, boxSizing: 'border-box' as const }}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                  <p style={{ fontSize: 10.5, color: neutral[400], margin: '3px 0 0' }}>Also local-only — Save review below is what actually records the reviewed flag and date.</p>
                </div>

                <label style={{ display: 'block', fontSize: 11, color: neutral[500], marginBottom: 3 }}>Review Conclusion</label>
                <textarea
                  value={reviewNotes}
                  onChange={e => setReviewNotes(e.target.value)}
                  rows={3}
                  placeholder="What did you find? e.g. 'Confirmed with site guard — theft flag was a meter reading error, not an actual loss.'"
                  aria-label="Review conclusion"
                  style={{
                    width: '100%', border: `1px solid ${neutral[200]}`, borderRadius: 8,
                    padding: '8px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box' as const,
                  }}
                />
                {reviewError && (
                  <p style={{ color: urgencyTokens.critical.fg, fontSize: 12, margin: '6px 0 0' }}>{reviewError}</p>
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={() => setShowReviewForm(false)}
                    disabled={reviewSaving}
                    style={{ fontSize: 12, fontWeight: 600, color: neutral[700], background: '#fff', border: `1px solid ${neutral[200]}`, borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleSaveReview}
                    disabled={reviewSaving || !reviewNotes.trim()}
                    style={{
                      fontSize: 12, fontWeight: 600, color: '#fff', background: ink, border: 'none',
                      borderRadius: 6, padding: '6px 14px', cursor: reviewSaving ? 'not-allowed' : 'pointer',
                      opacity: reviewSaving || !reviewNotes.trim() ? 0.6 : 1,
                    }}
                  >
                    {reviewSaving ? 'Saving…' : 'Save review'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

// ─── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: ink, margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: neutral[500], margin: '4px 0 0' }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>{actions}</div>}
    </div>
  );
}

// ─── Shared button ────────────────────────────────────────────────────────────
interface BtnProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
  /**
   * When true: disables the button, shows a small inline spinner before the
   * label, and sets aria-busy. Previously every page had to remember to
   * manually pass `disabled={someLoadingFlag}` during an async action —
   * inconsistently applied across the 16 diesel pages, risking double-submit
   * on a slow network (e.g. double-clicking "Approve" or "Save Mapping").
   * Purely opt-in — existing callers that don't pass this prop are unaffected.
   */
  loading?: boolean;
}
export function Btn({ variant = 'primary', size = 'md', children, style, loading = false, disabled, ...rest }: BtnProps) {
  const variants = {
    primary:   { background: brand.primary, color: '#fff', border: 'none' },
    secondary: { background: neutral[100], color: neutral[700], border: `1px solid ${neutral[200]}` },
    danger:    { background: statusTokens.danger.bg, color: statusTokens.danger.text, border: 'none' },
    ghost:     { background: 'none', color: neutral[700], border: `1px solid ${neutral[200]}` },
  };
  const sizes = { sm: { padding: '4px 12px', fontSize: typeScale.small }, md: { padding: '8px 18px', fontSize: typeScale.small + 1 } };
  const isDisabled = disabled || loading;
  return (
    <button
      aria-busy={loading || undefined}
      disabled={isDisabled}
      style={{
        borderRadius: radiusTokens.md, fontWeight: 600,
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        opacity: isDisabled ? 0.65 : 1,
        display: 'inline-flex', alignItems: 'center', gap: 6,
        ...variants[variant], ...sizes[size], ...style,
      }}
      {...rest}
    >
      {loading && (
        <span
          aria-hidden="true"
          style={{
            width: 12, height: 12, borderRadius: '50%',
            border: '2px solid currentColor', borderTopColor: 'transparent',
            display: 'inline-block', animation: 'diesel-btn-spin 0.7s linear infinite',
          }}
        />
      )}
      {children}
    </button>
  );
}
// Keyframe injected once — scoped by a unique name so it can't collide with
// other spin animations elsewhere in the app.
if (typeof document !== 'undefined' && !document.getElementById('diesel-btn-spin-kf')) {
  const styleTag = document.createElement('style');
  styleTag.id = 'diesel-btn-spin-kf';
  styleTag.textContent = '@keyframes diesel-btn-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(styleTag);
}

// ─── LoadingSpinner ───────────────────────────────────────────────────────────
export function Spinner({ size = 40 }: { size?: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 40 }}>
      <div style={{ width: size, height: size, border: '3px solid #e5e7eb', borderTopColor: ink, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}

// ─── ErrorBox ─────────────────────────────────────────────────────────────────
export function ErrorBox({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div style={{ background: statusTokens.danger.bg, border: '1px solid #fecaca', borderRadius: 10, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#fecaca', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <IconGlyph pathKey="x" size={16} color={statusTokens.danger.text} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontWeight: 600, color: statusTokens.danger.text, margin: 0 }}>Error</p>
        <p style={{ color: '#b91c1c', fontSize: 13, margin: '2px 0 0' }}>{message}</p>
      </div>
      {onRetry && <Btn variant="ghost" size="sm" onClick={onRetry}>Retry</Btn>}
    </div>
  );
}

// ─── Line icons ───────────────────────────────────────────────────────────────
// Line-icon paths lifted from the prototype's own icon sprite (thin 2px-stroke
// glyphs, no fill) — kept here rather than as a separate sprite file so these
// components have zero new dependencies. Every caller still just passes an
// `icon` emoji (unchanged call sites across the app, in EmptyState, KpiCard
// and UploadZone); EMOJI_ICON_MAP below translates that emoji to the matching
// line-icon key so the *rendered* glyph matches the prototype's visual
// language instead of showing a large colorful pictograph.
const ICON_PATHS: Record<string, string> = {
  clock: 'M12 12m-9 0a9 9 0 1 0 18 0 9 9 0 1 0-18 0M12 7v5l3 2',
  bell: 'M6 10a6 6 0 1 1 12 0c0 3 1 4.5 2 5.5H4c1-1 2-2.5 2-5.5zM9.5 18.5a2.5 2.5 0 0 0 5 0',
  x: 'M6 6l12 12M18 6L6 18',
  inbox: 'M4 12h4l2 3h4l2-3h4M5.5 5h13L21 12v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-6z',
  drop: 'M12 2.5s7 7.8 7 12.5a7 7 0 1 1-14 0c0-4.7 7-12.5 7-12.5z',
  fuel: 'M4 3h10v18H4zM14 8h2.5l3 3V18a1.3 1.3 0 0 1-2.6 0v-2.5H17M7 7h4',
  chart: 'M4 19V5M4 19h16M8 15l3-4 3 2 4-6',
  trend: 'M3 17l5-5 4 4 8-9M15 7h5v5',
  wallet: 'M3 7.5A2 2 0 0 1 5 5.5h13a2 2 0 0 1 2 2V17a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM15 12.5a1.4 1.4 0 1 0 0-.1zM3 9.5h18',
  card: 'M2.5 5.5h19v13h-19zM2.5 9.5h19',
  pin: 'M12 21s7-6.3 7-11.5A7 7 0 1 0 5 9.5C5 14.7 12 21 12 21z M12 9.5a2.4 2.4 0 1 1 0-.1z',
  scale: 'M12 3v18M7 21h10M12 5l-6 1.5M12 5l6 1.5M3 7l3 6a3 3 0 0 0 6 0L9 7M15 7l3 6a3 3 0 0 0 6 0l-3-6',
  cal: 'M3 4.5h18v16H3zM3 9.5h18M8 2.5v4M16 2.5v4',
  alert: 'M12 3L2 20h20zM12 10v4',
  search: 'M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14zM21 21l-4.3-4.3',
  cloud: 'M7 18a4.5 4.5 0 0 1-.6-8.96A5.5 5.5 0 0 1 17.2 8 4 4 0 0 1 17 16M12 12v6M9.5 15.5L12 18l2.5-2.5',
  engine: 'M3 8h13v9H3zM16 11h3l2 2v4h-5M7 19.5a1.5 1.5 0 1 1 0-.1M17 19.5a1.5 1.5 0 1 1 0-.1',
  map: 'M9 4l-5 2v14l5-2 6 2 5-2V4l-5 2-6-2zM9 4v14M15 6v14',
  note: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8v5l-.1.1M12 16h.01',
  cpu: 'M7 7h10v10H7zM9 2v3M15 2v3M9 19v3M15 19v3M2 9h3M2 15h3M19 9h3M19 15h3',
  check: 'M4 12l5 5 11-11',
  doc: 'M6 2.5h8l4 4V21a.5.5 0 0 1-.5.5H6.5A.5.5 0 0 1 6 21z M14 2.5V7h4M9 13h6M9 16h6',
  'check-shield': 'M12 3l7 3v6c0 5-3.5 7.5-7 9-3.5-1.5-7-4-7-9V6zM9 12l2 2 4-4',
};
const EMOJI_ICON_MAP: Record<string, keyof typeof ICON_PATHS> = {
  '📭': 'inbox', '📦': 'inbox', '📨': 'doc', '📸': 'doc', '📋': 'doc', '🧾': 'doc', '✏️': 'note',
  '💧': 'drop', '⛽': 'fuel', '🛢️': 'fuel',
  '📊': 'chart', '🧮': 'chart',
  '📈': 'trend', '📉': 'trend', '🔁': 'trend',
  '💰': 'wallet',
  '💳': 'card',
  '📍': 'pin', '🗺️': 'map', '🏘️': 'map',
  '⚖️': 'scale',
  '📅': 'cal',
  '🚨': 'alert', '⚠️': 'alert', '🔥': 'alert',
  '🔎': 'search', '🔍': 'search',
  '🔧': 'engine', '👷': 'engine', '⚡': 'engine',
  '🔌': 'cpu',
  '✓': 'check',
  '✅': 'check-shield', '🔒': 'check-shield',
  '❓': 'note',
  '⏱️': 'clock', '⏳': 'clock',
  '📂': 'cloud',
  '📡': 'cpu', '🧑‍🔧': 'engine',
};

function IconGlyph({ pathKey, size, color }: { pathKey: keyof typeof ICON_PATHS; size: number; color: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d={ICON_PATHS[pathKey]} />
    </svg>
  );
}

// Shared by KpiCard / UploadZone / EmptyState: resolve a caller's emoji prop
// (call sites are unchanged everywhere) to a line-icon glyph, falling back to
// the raw emoji itself only if it isn't in the map (so nothing goes blank).
function ResolvedIcon({ icon, size, color }: { icon: string; size: number; color: string }) {
  const pathKey = EMOJI_ICON_MAP[icon];
  return pathKey ? <IconGlyph pathKey={pathKey} size={size} color={color} /> : <span style={{ fontSize: size }}>{icon}</span>;
}
// Exported so individual pages (e.g. a timeline mixing several event types)
// can render the same line-icon glyphs directly, instead of each page
// re-declaring its own emoji → meaning map.
export function Icon({ icon, size = 16, color = neutral[400] }: { icon: string; size?: number; color?: string }) {
  return <ResolvedIcon icon={icon} size={size} color={color} />;
}

export function EmptyState({ icon = '📭', title, desc }: { icon?: string; title: string; desc?: string }) {
  const pathKey = EMOJI_ICON_MAP[icon] || 'inbox';
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: neutral[400] }}>
      <div
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: neutral[100],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 14px',
        }}
      >
        <IconGlyph pathKey={pathKey} size={26} color={neutral[400]} />
      </div>
      <p style={{ fontWeight: 600, color: neutral[700], margin: '0 0 6px' }}>{title}</p>
      {desc && <p style={{ fontSize: 13, margin: 0 }}>{desc}</p>}
    </div>
  );
}

// ─── Card ─────────────────────────────────────────────────────────────────────
// Generic content card — the same chrome KpiCard already uses (border,
// radius, resting shadow), pulled out so a new screen's body panels (a
// framing note, a chart, a table wrapper) get consistent chrome without
// re-declaring the same inline style object on every page.
export function Card({ children, padded = true, style }: { children: React.ReactNode; padded?: boolean; style?: React.CSSProperties }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${neutral[200]}`, borderRadius: 12, boxShadow: shadowTokens.card, padding: padded ? '18px 22px' : 0, ...style }}>
      {children}
    </div>
  );
}

// ─── Tag ──────────────────────────────────────────────────────────────────────
// A generic label with five semantic variants, for short arbitrary phrases
// StatusPill's fixed keyword map (completed/open/pending/…) doesn't cover —
// e.g. "Gain — within contract", "Approved · en route". StatusPill stays as
// the right choice wherever one of its exact keywords applies; Tag is for
// everything else that still needs a success/warning/danger/info/neutral
// read at a glance.
export type TagVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';
const TAG_STYLE: Record<TagVariant, { bg: string; text: string }> = {
  success: statusTokens.success,
  warning: statusTokens.warning,
  danger:  statusTokens.danger,
  info:    statusTokens.info,
  neutral: statusTokens.neutral,
};
export function Tag({ variant = 'neutral', dot, children }: { variant?: TagVariant; dot?: boolean; children: React.ReactNode }) {
  const s = TAG_STYLE[variant];
  return (
    <span style={{ background: s.bg, color: s.text, padding: '2px 10px', borderRadius: radiusTokens.pill, fontSize: typeScale.small - 1, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: s.text, display: 'inline-block', flexShrink: 0 }} />}
      {children}
    </span>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
// A vertical list of dated events (visit history, delivery history, review
// history) — the event-log layout several pages need, so it's built once
// instead of hand-rolled per page.
export interface TimelineEvent {
  title: string;
  sub?: string;
  tone?: 'neutral' | 'ok' | 'warn' | 'crit';
}
const TIMELINE_DOT: Record<NonNullable<TimelineEvent['tone']>, string> = {
  neutral: neutral[400],
  ok:      urgencyTokens.ok.fg,
  warn:    urgencyTokens.high.fg,
  crit:    urgencyTokens.critical.fg,
};
export function Timeline({ events }: { events: TimelineEvent[] }) {
  return (
    <div>
      {events.map((e, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, paddingBottom: i < events.length - 1 ? 14 : 0 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0 }}>
            <span style={{ width: 9, height: 9, borderRadius: '50%', background: TIMELINE_DOT[e.tone || 'neutral'], marginTop: 3, flexShrink: 0 }} />
            {i < events.length - 1 && <span style={{ width: 1, flex: 1, background: neutral[200], marginTop: 2 }} />}
          </div>
          <div style={{ paddingBottom: 2 }}>
            <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: ink }}>{e.title}</p>
            {e.sub && <p style={{ margin: '2px 0 0', fontSize: 11.5, color: neutral[500] }}>{e.sub}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── ApprovalChain ────────────────────────────────────────────────────────────
// A horizontal step chain showing where a request sits in a fixed approval
// sequence (e.g. Supervisor → Finance → Head of Business → CEO) — done /
// current / rejected states, so the whole chain's status reads at a glance
// instead of a status pill plus a mental model of the order it implies.
export interface ApprovalStep {
  label: string;
  state: 'done' | 'current' | 'pending' | 'rejected';
}
const STEP_STYLE: Record<ApprovalStep['state'], { bg: string; text: string; border: string }> = {
  done:     { bg: urgencyTokens.ok.bg,       text: urgencyTokens.ok.fg,       border: urgencyTokens.ok.fg },
  current:  { bg: urgencyTokens.medium.bg,   text: urgencyTokens.medium.fg,   border: urgencyTokens.medium.fg },
  pending:  { bg: neutral[100],              text: neutral[400],             border: neutral[200] },
  rejected: { bg: urgencyTokens.critical.bg, text: urgencyTokens.critical.fg, border: urgencyTokens.critical.fg },
};
export function ApprovalChain({ steps, ariaLabel }: { steps: ApprovalStep[]; ariaLabel?: string }) {
  return (
    <div role="list" aria-label={ariaLabel} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0 }}>
      {steps.map((s, i) => (
        <React.Fragment key={i}>
          <span
            role="listitem"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: radiusTokens.pill,
              background: STEP_STYLE[s.state].bg, color: STEP_STYLE[s.state].text,
              border: `1px solid ${STEP_STYLE[s.state].border}`,
            }}
          >
            {s.state === 'done' && '✓'}
            {s.state === 'rejected' && '✕'}
            {s.label}
          </span>
          {i < steps.length - 1 && (
            <span aria-hidden="true" style={{ width: 14, height: 1, background: neutral[300], flexShrink: 0 }} />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── DetailGrid ───────────────────────────────────────────────────────────────
// The label/value grid SiteDetailDrawer already builds inline — pulled out
// so a new detail panel (e.g. the Gain/Loss Tracker's per-site drawer) gets
// the same layout without copy-pasting the markup again. SiteDetailDrawer
// itself is left as-is for now rather than risking a refactor of a working,
// in-use component outside this batch's scope.
export function DetailGrid({ rows }: { rows: Array<[string, React.ReactNode]> }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px' }}>
      {rows.map(([label, value], i) => (
        <div key={`${label}-${i}`} style={{ borderBottom: '1px solid #f3f4f6', paddingBottom: 8 }}>
          <p style={{ fontSize: 11, color: neutral[400], margin: '0 0 2px' }}>{label}</p>
          <p style={{ fontSize: 13, fontWeight: 500, margin: 0, color: ink }}>{value ?? '—'}</p>
        </div>
      ))}
    </div>
  );
}

